<script setup lang="ts">
import KpToolbar from '@/components/kp-toolbar.vue'
import KpFieldlabel from '@/components/kp-fieldlabel.vue'
import Editor from '@/components/html-editor/Editor.vue'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { instance as api } from '@/helpers/api'
import { registerFormRouteGuard } from '@/composables/routerGuard'
import { errToStr } from '@/helpers/tools'
import { useToast } from '@/composables/toast'

const { t } = useI18n()
const toast = useToast()

const linkExample = 'https://www.beispiel.ch'
const mailExample = 'mailto:info@beispiel.ch'
const telExample = 'tel:058 345 54 80'

const initialLoading = ref(false)
const loading = ref(false)

const afe = ref('')
const tg = ref('')
const sh = ref('')

const savedAfe = ref('')
const savedTg = ref('')
const savedSh = ref('')

registerFormRouteGuard(isDirty)

onMounted(loadTexts)

async function loadTexts() {
    initialLoading.value = true
    try {
        const res = await api().get('/hilfetext/load')
        afe.value = res.data.afe
        tg.value = res.data.tg
        sh.value = res.data.sh
        savedAfe.value = afe.value
        savedTg.value = tg.value
        savedSh.value = sh.value
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        initialLoading.value = false
    }
}

function isDirty() {
    return afe.value !== savedAfe.value || tg.value !== savedTg.value || sh.value !== savedSh.value
}

async function onSubmit() {
    loading.value = true
    try {
        await api().post('/hilfetext/store', {
            afe: afe.value,
            tg: tg.value,
            sh: sh.value,
        })
        savedAfe.value = afe.value
        savedTg.value = tg.value
        savedSh.value = sh.value
        toast.add({ text: t('gespeichert'), color: 'success' })
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <KpToolbar icon="fas fa-circle-question" :title="t('hilfetexte')"> </KpToolbar>
    <v-skeleton-loader
        class="centered-card"
        v-if="initialLoading"
        type="list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <v-container fluid>
            <v-alert
                type="info"
                :icon="false"
                border="start"
                colored-border
                variant="outlined"
                elevation="2"
                class="centered-card mb-4"
            >
                <div class="mb-2">{{ t('hilfetexte_hinweis') }}</div>
                <div class="link-example">
                    <strong>{{ t('hilfetexte_link_normal') }}:</strong>
                    <code>{{ linkExample }}</code>
                </div>
                <div class="link-example">
                    <strong>{{ t('hilfetexte_link_email') }}:</strong>
                    <code>{{ mailExample }}</code>
                </div>
                <div class="link-example">
                    <strong>{{ t('hilfetexte_link_tel') }}:</strong>
                    <code>{{ telExample }}</code>
                </div>
                <div class="mt-3">{{ t('hilfetexte_listen_hinweis') }}</div>
            </v-alert>
            <v-card class="centered-card">
                <v-card-text>
                    <KpFieldlabel :label="t('hilfetexte_tg')" />
                    <Editor v-model:model-value="tg" class="mt-2" />
                    <KpFieldlabel :label="t('hilfetexte_sh')" />
                    <Editor v-model:model-value="sh" class="mt-2" />
                    <KpFieldlabel :label="t('hilfetexte_energiefoerderportal')" />
                    <Editor v-model:model-value="afe" class="mt-2" />
                </v-card-text>
                <v-card-actions class="ml-4">
                    <v-spacer />
                    <v-btn
                        class="hidden-xs"
                        variant="flat"
                        color="action white--text"
                        :disabled="loading"
                        :loading="loading"
                        @click="onSubmit"
                        prepend-icon="fa-solid fa-floppy-disk"
                    >
                        {{ t('save') }}
                    </v-btn>
                    <v-btn
                        class="hidden-sm-and-up"
                        color="action white--text"
                        :disabled="loading"
                        :loading="loading"
                        @click="onSubmit"
                        icon="fa-solid fa-floppy-disk"
                    >
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-container>
    </div>
</template>

<style lang="css" scoped>
.link-example {
    margin-top: 0.25rem;
}
.link-example code {
    margin-left: 0.5rem;
    word-break: break-all;
}
</style>
